import {Modal, Button, Spinner, Form} from 'react-bootstrap'
import Select from 'react-select'
import {defineMessages, useIntl} from 'react-intl'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {useMutation, useQuery} from '@apollo/client'
import {parseId} from '../../../helpers'
import {IResAllListUsers} from '../../../types/users'
import {GET_ALL_USERS_LIST} from '../../../gql/queries/usersQuery'
import {REASSIGN_POLICIES_USER} from '../../../gql/mutations/policyMutation'
import {PolicyFilters} from '../policiesTypes'
import {useGetUser} from '../../../store/userStore'
import {GET_ALL_GROUPS} from '../../../gql/queries/assignmentGroupQuery'
import {IResGroups} from '../../../types/assignmentGroups'
import {AppliedFiltersList, FilterConfig, DateRangeFilter} from './AppliedFiltersList'

const M = defineMessages({
  title: {id: 'policies.list.reassign.title', defaultMessage: 'Reassign policies'},
  subtitle: {id: 'policies.list.reassign.subtitle', defaultMessage: 'Destination uses round robin when more than one user is eligible.'},
  sectionOrigin: {id: 'policies.list.reassign.section.origin', defaultMessage: 'Origin'},
  sectionDestination: {id: 'policies.list.reassign.section.destination', defaultMessage: 'Destination'},
  srcBySelectedUser: {id: 'policies.list.reassign.src.bySelectedUser', defaultMessage: 'By selected user'},
  srcByCurrentFilters: {id: 'policies.list.reassign.src.byCurrentFilters', defaultMessage: 'By current filters'},
  from: {id: 'policies.list.reassign.from', defaultMessage: 'From'},
  phSelectUser: {id: 'policies.list.reassign.placeholder.selectUser', defaultMessage: 'Please select a user'},
  emptyNoFilters: {id: 'policies.list.reassign.emptyNoFilters', defaultMessage: 'No filters selected on the policies page.'},
  destUsers: {id: 'policies.list.reassign.dest.users', defaultMessage: 'One or more users'},
  assignmentGroup: {id: 'policies.list.reassign.assignmentGroup', defaultMessage: 'Assignment group'},
  toUsers: {id: 'policies.list.reassign.toUsers', defaultMessage: 'To users'},
  phSelectUsers: {id: 'policies.list.reassign.placeholder.selectUsers', defaultMessage: 'Please select one or more users'},
  phSelectGroup: {id: 'policies.list.reassign.placeholder.selectGroup', defaultMessage: 'Please select a group'},
  noOptsSupervised: {id: 'policies.list.reassign.noOpts.supervised', defaultMessage: 'No supervised groups with eligible users available'},
  noOptsGeneral: {id: 'policies.list.reassign.noOpts.general', defaultMessage: 'No groups with supervisors and eligible users available'},
  hintSupervisor: {id: 'policies.list.reassign.hint.supervisor', defaultMessage: 'You can reassign only to groups where you are configured as supervisor.'},
  hintGeneral: {id: 'policies.list.reassign.hint.general', defaultMessage: 'You can reassign to any assignment group that has at least one supervisor.'},
  reassignBtn: {id: 'policies.list.reassign.btn', defaultMessage: 'Reassign Policies'},
  cancel: {id: 'common.action.cancel', defaultMessage: 'Cancel'},
  // Shared filter-source badges (reused from the update modal catalog).
  badgeImportant: {id: 'policies.list.update.badge.important', defaultMessage: 'Important'},
  badgeArchived: {id: 'policies.list.update.badge.archived', defaultMessage: 'Archived'},
  badgeHasReminders: {id: 'policies.list.update.badge.hasReminders', defaultMessage: 'Has reminders'},
  badgeYears: {id: 'policies.list.update.badge.years', defaultMessage: 'Years: {years}'},
  badgeMember: {id: 'policies.list.update.badge.member', defaultMessage: 'Member: {value}'},
  badgePolicy: {id: 'policies.list.update.badge.policy', defaultMessage: 'Policy: {value}'},
})

interface ModalReassignPoliciesProps {
  isOpen: boolean
  onClose: () => void
  updateListPolicies: () => void
  filters: PolicyFilters
  tabFilters: string
  selectedYears: Array<string | number>
  searchByMembers: boolean
  filtersConfig: FilterConfig[]
  dateRangeFilters: DateRangeFilter[]
  formatDate: (date: string) => string
}

interface IResAssignPolicies {
  assignPoliciesToUser: {
    success: boolean
    message: string
    errors?: {field: string; message: string}[]
  }
}

interface UserOption {
  value: string
  label: string
}

interface GroupOption {
  value: string
  label: string
}

type SourceMode = 'by-user' | 'by-filters'
type DestinationMode = 'users' | 'group'

const ALLOWED_PRIMARY_GROUPS = new Set([
  'Asesor',
  'Supervisor',
  'Administrador',
  'SuperAdministrador',
])

const toArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item)).filter(Boolean)
  }
  if (value === null || value === undefined || value === '') {
    return []
  }
  return [String(value)]
}

const toIdArray = (value: unknown): string[] => {
  return toArray(value)
    .map((item) => {
      const parsed = parseId(item)
      return parsed ? String(parsed) : item
    })
    .filter(Boolean)
}

const toNumericId = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') {
    return null
  }
  const parsed = parseId(value)
  const numeric = typeof parsed === 'number' ? parsed : Number(parsed)
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null
}

export const buildPolicyReassignFilterVariables = (
  filters: PolicyFilters,
  tabFilters: string,
  selectedYears: Array<string | number>,
  searchByMembers: boolean
) => {
  return {
    policyName: undefined,
    isImportant: tabFilters === 'Important' ? true : undefined,
    archived: tabFilters === 'Archived' ? true : undefined,
    hasReminders: tabFilters === 'Reminders' ? true : undefined,
    hasMemberId: filters.hasMemberId,
    commissionPayable: filters.commissionPayable,
    advisorPaid: filters.advisorPaid,
    advisorChargeback: filters.advisorChargeback,
    unassigned: filters.unassigned,
    ownsCar: filters.ownsCar,
    ownsHome: filters.ownsHome,
    hasAutoPolicy: filters.hasAutoPolicy,
    hasHomePolicy: filters.hasHomePolicy,
    carrier: toIdArray(filters.carrier),
    insuranceCategory: filters.insuranceCategory || undefined,
    policyType: toIdArray(filters.policyType),
    user: toIdArray(filters.user),
    assignedTo: toIdArray(filters.assignedTo),
    zipCode: filters.zipCode || undefined,
    stateEU: toArray(filters.stateEU),
    stateJurisdictionType: filters.stateJurisdictionType || undefined,
    status: toArray(filters.status),
    paymentStatus: toArray(filters.paymentStatus),
    documentStatus: toArray(filters.documentStatus),
    leadSource: toIdArray(filters.leadSource),
    signatureForm: toIdArray(filters.signatureForm),
    signatureStatus: filters.signatureStatus || undefined,
    agentOfRecord: toIdArray(filters.agentOfRecord),
    immigrationStatus: toArray(filters.immigrationStatus),
    holderGender: filters.holderGender || undefined,
    metalLevel: filters.metalLevel || undefined,
    createdAfter: filters.createdAfter || undefined,
    createdBefore: filters.createdBefore || undefined,
    effectiveDateAfter: filters.effectiveDateAfter || undefined,
    effectiveDateBefore: filters.effectiveDateBefore || undefined,
    documentsDueDateAfter: filters.documentsDueDateAfter || undefined,
    documentsDueDateBefore: filters.documentsDueDateBefore || undefined,
    paymentStatusDateAfter: filters.paymentStatusDateAfter || undefined,
    paymentStatusDateBefore: filters.paymentStatusDateBefore || undefined,
    advisorPaidDateAfter: filters.advisorPaidDateAfter || undefined,
    advisorPaidDateBefore: filters.advisorPaidDateBefore || undefined,
    advisorChargebackDateAfter: filters.advisorChargebackDateAfter || undefined,
    advisorChargebackDateBefore: filters.advisorChargebackDateBefore || undefined,
    canceledAfter: filters.canceledAfter || undefined,
    canceledBefore: filters.canceledBefore || undefined,
    completedAfter: filters.completedAfter || undefined,
    completedBefore: filters.completedBefore || undefined,
    effectiveYear: selectedYears.length > 0 ? selectedYears : undefined,
    holderName: searchByMembers ? undefined : filters.policyName || undefined,
    memberName: searchByMembers ? filters.policyName || undefined : undefined,
    carrierStatusGeneric: toArray(filters.carrierStatusGeneric),
    carrierStatusRaw: filters.carrierStatusRaw || undefined,
    carrierLastAsOfDateAfter: filters.carrierLastAsOfDateAfter || undefined,
    carrierLastAsOfDateBefore: filters.carrierLastAsOfDateBefore || undefined,
    carrierIsAgeIn: filters.carrierIsAgeIn,
    carrierIsDelinquent: filters.carrierIsDelinquent,
    carrierIsUnpaidPending: filters.carrierIsUnpaidPending,
    carrierIsUnpaidPastDue: filters.carrierIsUnpaidPastDue,
  }
}

export const getActiveFilterSummary = (
  filters: PolicyFilters,
  tabFilters: string,
  selectedYears: Array<string | number>,
  searchByMembers: boolean
) => {
  const summary: string[] = []

  if (tabFilters === 'Important') summary.push('Important')
  if (tabFilters === 'Archived') summary.push('Archived')
  if (tabFilters === 'Reminders') summary.push('Has reminders')
  if (filters.policyName) summary.push(`${searchByMembers ? 'Member' : 'Policy'}: ${filters.policyName}`)
  if (selectedYears.length > 0) summary.push(`Years: ${selectedYears.join(', ')}`)

  const arrayLabels: Array<[string, unknown]> = [
    ['Assigned to', filters.assignedTo],
    ['Agent', filters.user],
    ['Carrier', filters.carrier],
    ['Policy type', filters.policyType],
    ['Lead source', filters.leadSource],
    ['Status', filters.status],
    ['Payment status', filters.paymentStatus],
    ['Document status', filters.documentStatus],
    ['State', filters.stateEU],
    ['Immigration status', filters.immigrationStatus],
    ['Signature form', filters.signatureForm],
    ['Agent of record', filters.agentOfRecord],
    ['Carrier status', filters.carrierStatusGeneric],
  ]

  arrayLabels.forEach(([label, value]) => {
    const values = toArray(value)
    if (values.length > 0) {
      summary.push(`${label}: ${values.length}`)
    }
  })

  const scalarLabels: Array<[string, unknown]> = [
    ['Insurance category', filters.insuranceCategory],
    ['Zip code', filters.zipCode],
    ['State jurisdiction', filters.stateJurisdictionType],
    ['Signature status', filters.signatureStatus],
    ['Holder gender', filters.holderGender],
    ['Metal level', filters.metalLevel],
    ['Carrier raw status', filters.carrierStatusRaw],
  ]

  scalarLabels.forEach(([label, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      summary.push(`${label}: ${String(value)}`)
    }
  })

  const booleanLabels: Array<[string, boolean | undefined]> = [
    ['Has member id', filters.hasMemberId],
    ['Commission payable', filters.commissionPayable],
    ['Advisor paid', filters.advisorPaid],
    ['Advisor chargeback', filters.advisorChargeback],
    ['Unassigned', filters.unassigned],
    ['Owns car', filters.ownsCar],
    ['Owns home', filters.ownsHome],
    ['Has auto policy', filters.hasAutoPolicy],
    ['Has home policy', filters.hasHomePolicy],
    ['Carrier is age in', filters.carrierIsAgeIn],
    ['Carrier is delinquent', filters.carrierIsDelinquent],
    ['Carrier unpaid pending', filters.carrierIsUnpaidPending],
    ['Carrier unpaid past due', filters.carrierIsUnpaidPastDue],
  ]

  booleanLabels.forEach(([label, value]) => {
    if (value === true) summary.push(`${label}: Yes`)
    if (value === false) summary.push(`${label}: No`)
  })

  const dateLabels: Array<[string, string | undefined]> = [
    ['Created from', filters.createdAfter],
    ['Created to', filters.createdBefore],
    ['Effective from', filters.effectiveDateAfter],
    ['Effective to', filters.effectiveDateBefore],
    ['Documents due from', filters.documentsDueDateAfter],
    ['Documents due to', filters.documentsDueDateBefore],
    ['Payment status from', filters.paymentStatusDateAfter],
    ['Payment status to', filters.paymentStatusDateBefore],
    ['Advisor paid from', filters.advisorPaidDateAfter],
    ['Advisor paid to', filters.advisorPaidDateBefore],
    ['Chargeback from', filters.advisorChargebackDateAfter],
    ['Chargeback to', filters.advisorChargebackDateBefore],
    ['Canceled from', filters.canceledAfter],
    ['Canceled to', filters.canceledBefore],
    ['Completed from', filters.completedAfter],
    ['Completed to', filters.completedBefore],
    ['Carrier as-of from', filters.carrierLastAsOfDateAfter],
    ['Carrier as-of to', filters.carrierLastAsOfDateBefore],
  ]

  dateLabels.forEach(([label, value]) => {
    if (value) summary.push(`${label}: ${value}`)
  })

  return summary
}

export const ModalReassignPolicies = ({
  isOpen,
  onClose,
  updateListPolicies,
  filters,
  tabFilters,
  selectedYears,
  searchByMembers,
  filtersConfig,
  dateRangeFilters,
  formatDate,
}: ModalReassignPoliciesProps) => {
  const intl = useIntl()
  const authUser = useGetUser((s) => s.user)
  const currentUserId = authUser?.user?.id ? parseId(authUser.user.id) : null
  const userGroups = authUser?.groups || []
  const isSupervisorOnly =
    userGroups.includes('Supervisor') &&
    !userGroups.includes('Administrador') &&
    !userGroups.includes('SuperAdministrador')

  const [sourceMode, setSourceMode] = useState<SourceMode>('by-user')
  const [destinationMode, setDestinationMode] = useState<DestinationMode>('users')
  const [userFrom, setUserFrom] = useState<UserOption | null>(null)
  const [usersTo, setUsersTo] = useState<UserOption[]>([])
  const [groupTo, setGroupTo] = useState<GroupOption | null>(null)

  const activeFilterSummary = useMemo(
    () => getActiveFilterSummary(filters, tabFilters, selectedYears, searchByMembers),
    [filters, searchByMembers, selectedYears, tabFilters]
  )
  const hasFilterSource = activeFilterSummary.length > 0
  const filteredAssignedToIds = useMemo(() => toIdArray(filters.assignedTo), [filters.assignedTo])
  const excludedSourceUserId = useMemo(() => {
    if (sourceMode === 'by-user') {
      return parseId(userFrom?.value || '')
    }
    if (sourceMode === 'by-filters' && filteredAssignedToIds.length === 1) {
      return parseId(filteredAssignedToIds[0])
    }
    return null
  }, [filteredAssignedToIds, sourceMode, userFrom])

  const {
    data: dataUsers,
    loading: loadingUsers,
    error: errorUsers,
  } = useQuery<IResAllListUsers>(GET_ALL_USERS_LIST, {
    fetchPolicy: 'no-cache',
  })

  const {
    data: dataGroups,
    loading: loadingGroups,
    error: errorGroups,
  } = useQuery<IResGroups>(GET_ALL_GROUPS, {
    fetchPolicy: 'no-cache',
    variables: {first: 100},
  })

  const [gqlCall, {data, loading, error}] = useMutation<IResAssignPolicies>(REASSIGN_POLICIES_USER)

  const supervisedUserIds = useMemo(() => {
    if (!isSupervisorOnly || !currentUserId) return null

    const ids = new Set<number>()
    const groups = dataGroups?.allGroupAssignments?.edges || []

    groups.forEach(({node}) => {
      const isSupervisorOfGroup = node.supervisors?.edges?.some(
        ({node: supervisor}) => parseId(supervisor.id) === currentUserId
      )

      if (!isSupervisorOfGroup) return

      node.user?.edges?.forEach(({node: member}) => {
        const parsedMemberId = parseId(member.id)
        const memberId =
          typeof parsedMemberId === 'number' ? parsedMemberId : Number(parsedMemberId)
        if (memberId) ids.add(memberId)
      })
    })

    return ids
  }, [currentUserId, dataGroups, isSupervisorOnly])

  const userOptions = useMemo<UserOption[]>(() => {
    const users = dataUsers?.allUsersList || []
    return users
      .filter((item) => {
        if (!ALLOWED_PRIMARY_GROUPS.has(item.primaryGroup?.name || '')) return false
        if (!isSupervisorOnly || !supervisedUserIds) return true
        const parsedItemId = parseId(item.id)
        const itemId = typeof parsedItemId === 'number' ? parsedItemId : Number(parsedItemId)
        return supervisedUserIds.has(itemId || 0)
      })
      .map((item) => ({
        value: item.id,
        label: `${item.firstName} ${item.lastName} - ${item.email}${item.npn ? ` (${item.npn})` : ''}`,
      }))
  }, [dataUsers, isSupervisorOnly, supervisedUserIds])

  const destinationUserOptions = useMemo<UserOption[]>(() => {
    return userOptions.filter((item) => parseId(item.value) !== excludedSourceUserId)
  }, [excludedSourceUserId, userOptions])

  const groupOptions = useMemo<GroupOption[]>(() => {
    const groups = dataGroups?.allGroupAssignments?.edges || []

    return groups
      .filter(({node}) => {
        const hasSupervisors = (node.supervisors?.edges?.length || 0) > 0
        if (!hasSupervisors) return false
        if (!isSupervisorOnly) return true
        if (!currentUserId) return false
        return node.supervisors.edges.some(
          ({node: supervisor}) => parseId(supervisor.id) === currentUserId
        )
      })
      .filter(({node}) => {
        const availableUsers = node.user?.edges
          ?.map(({node: member}) => parseId(member.id))
          .filter((memberId) => memberId && memberId !== parseId(userFrom?.value || ''))
          .filter((memberId) => memberId !== excludedSourceUserId)
        return (availableUsers?.length || 0) > 0
      })
      .map(({node}) => {
        const eligibleCount =
          node.user?.edges.filter(
            ({node: member}) => parseId(member.id) !== excludedSourceUserId
          ).length || 0

        return {
          value: node.id,
          label: `${node.name} (${eligibleCount})`,
        }
      })
  }, [currentUserId, dataGroups, excludedSourceUserId, isSupervisorOnly])

  useEffect(() => {
    if (!hasFilterSource && sourceMode === 'by-filters') {
      setSourceMode('by-user')
    }
  }, [hasFilterSource, sourceMode])

  useEffect(() => {
    setUsersTo((current) =>
      current.filter((item) => parseId(item.value) !== excludedSourceUserId)
    )
    setGroupTo(null)
  }, [excludedSourceUserId])

  useEffect(() => {
    if (destinationMode === 'users') {
      setGroupTo(null)
    } else {
      setUsersTo([])
    }
  }, [destinationMode])

  useEffect(() => {
    const errMsg = (m: string) =>
      intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message: m})
    if (error) toast.error(errMsg(error.message))
    if (errorUsers) toast.error(errMsg(errorUsers.message))
    if (errorGroups) toast.error(errMsg(errorGroups.message))
  }, [error, errorGroups, errorUsers, intl])

  const disabledAssign = useMemo(() => {
    const validSource =
      sourceMode === 'by-user' ? !!userFrom : hasFilterSource

    const validDestination =
      destinationMode === 'users' ? usersTo.length > 0 : !!groupTo

    return !(validSource && validDestination)
  }, [destinationMode, groupTo, hasFilterSource, sourceMode, userFrom, usersTo.length])

  const modalClose = useCallback(() => {
    setSourceMode(hasFilterSource ? 'by-filters' : 'by-user')
    setDestinationMode('users')
    setUserFrom(null)
    setUsersTo([])
    setGroupTo(null)
    onClose()
  }, [hasFilterSource, onClose])

  useEffect(() => {
    if (!isOpen) return
    setSourceMode(hasFilterSource ? 'by-filters' : 'by-user')
  }, [hasFilterSource, isOpen])

  useEffect(() => {
    if (!data) return
    if (data.assignPoliciesToUser?.success) {
      toast.success(data.assignPoliciesToUser.message)
      updateListPolicies()
      modalClose()
      return
    }

    const message = data.assignPoliciesToUser?.errors?.[0]?.message
    if (message) {
      toast.error(intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message}))
    }
  }, [data, modalClose, updateListPolicies, intl])

  const handleReAssignPolicies = () => {
    if (disabledAssign) return

    const variables: Record<string, unknown> = {}

    if (sourceMode === 'by-user') {
      variables.fromUserId = toNumericId(userFrom?.value || '')
    } else {
      Object.assign(
        variables,
        buildPolicyReassignFilterVariables(filters, tabFilters, selectedYears, searchByMembers)
      )
    }

    if (destinationMode === 'users') {
      variables.toUserIds = usersTo
        .map((item) => toNumericId(item.value))
        .filter((value): value is number => value !== null)
    } else {
      variables.toGroupId = toNumericId(groupTo?.value || '')
    }

    gqlCall({variables})
  }

  return (
    <Modal show={isOpen} onHide={modalClose} size='xl' className='modal-reassign-policies'>
      <Modal.Header closeButton>
        <Modal.Title>
          <div className='d-flex flex-column gap-0'>
            <span className='fw-bold'>{intl.formatMessage(M.title)}</span>
            <span className='text-muted fs-7'>
              {intl.formatMessage(M.subtitle)}
            </span>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='d-flex flex-column gap-4'>
          <div>
            <h2 className='fs-3 mb-3'>{intl.formatMessage(M.sectionOrigin)}</h2>
            <div className='row g-3'>
              <div className='col-md-4'>
                <Form.Check
                  type='radio'
                  id='reassign-policies-source-user'
                  name='reassignPoliciesSource'
                  label={intl.formatMessage(M.srcBySelectedUser)}
                  checked={sourceMode === 'by-user'}
                  onChange={() => setSourceMode('by-user')}
                />
              </div>
              <div className='col-md-8'>
                <Form.Check
                  type='radio'
                  id='reassign-policies-source-filters'
                  name='reassignPoliciesSource'
                  label={intl.formatMessage(M.srcByCurrentFilters)}
                  checked={sourceMode === 'by-filters'}
                  disabled={!hasFilterSource}
                  onChange={() => setSourceMode('by-filters')}
                />
              </div>
            </div>

            {sourceMode === 'by-user' && (
              <div className='mt-3'>
                <div className='mb-2 fw-semibold'>
                  {intl.formatMessage(M.from)} {loadingUsers && (
                    <span>
                      <Spinner animation='border' size='sm' />
                    </span>
                  )}
                </div>
                <Select<UserOption, false>
                  value={userFrom}
                  options={userOptions}
                  onChange={(selected) => setUserFrom(selected)}
                  placeholder={intl.formatMessage(M.phSelectUser)}
                  isClearable
                  isSearchable
                />
              </div>
            )}

            {sourceMode === 'by-filters' && (
              <div className='mt-3 rounded border p-3 bg-light'>
                {hasFilterSource ? (
                  <div className='d-flex flex-column gap-2'>
                    {(tabFilters === 'Important' || tabFilters === 'Archived' || tabFilters === 'Reminders' || selectedYears.length > 0 || filters.policyName) && (
                      <div className='d-flex flex-wrap gap-2'>
                        {tabFilters === 'Important' && <span className='badge bg-secondary-subtle text-dark'>{intl.formatMessage(M.badgeImportant)}</span>}
                        {tabFilters === 'Archived' && <span className='badge bg-secondary-subtle text-dark'>{intl.formatMessage(M.badgeArchived)}</span>}
                        {tabFilters === 'Reminders' && <span className='badge bg-secondary-subtle text-dark'>{intl.formatMessage(M.badgeHasReminders)}</span>}
                        {selectedYears.length > 0 && <span className='badge bg-secondary-subtle text-dark'>{intl.formatMessage(M.badgeYears, {years: selectedYears.join(', ')})}</span>}
                        {filters.policyName && <span className='badge bg-secondary-subtle text-dark'>{searchByMembers ? intl.formatMessage(M.badgeMember, {value: filters.policyName}) : intl.formatMessage(M.badgePolicy, {value: filters.policyName})}</span>}
                      </div>
                    )}
                    <AppliedFiltersList
                      filters={filters}
                      config={filtersConfig}
                      dateRangeFilters={dateRangeFilters}
                      formatDate={formatDate}
                    />
                  </div>
                ) : (
                  <span className='text-muted'>{intl.formatMessage(M.emptyNoFilters)}</span>
                )}
              </div>
            )}
          </div>

          <div>
            <h2 className='fs-3 mb-3'>{intl.formatMessage(M.sectionDestination)}</h2>
            <div className='row g-3'>
              <div className='col-md-4'>
                <Form.Check
                  type='radio'
                  id='reassign-policies-destination-users'
                  name='reassignPoliciesDestination'
                  label={intl.formatMessage(M.destUsers)}
                  checked={destinationMode === 'users'}
                  onChange={() => setDestinationMode('users')}
                />
              </div>
              <div className='col-md-8'>
                <Form.Check
                  type='radio'
                  id='reassign-policies-destination-group'
                  name='reassignPoliciesDestination'
                  label={intl.formatMessage(M.assignmentGroup)}
                  checked={destinationMode === 'group'}
                  onChange={() => setDestinationMode('group')}
                />
              </div>
            </div>

            {destinationMode === 'users' && (
              <div className='mt-3'>
                <div className='mb-2 fw-semibold'>
                  {intl.formatMessage(M.toUsers)} {loadingUsers && (
                    <span>
                      <Spinner animation='border' size='sm' />
                    </span>
                  )}
                </div>
                <Select<UserOption, true>
                  value={usersTo}
                  options={destinationUserOptions}
                  onChange={(selected) => setUsersTo(selected ? [...selected] : [])}
                  placeholder={intl.formatMessage(M.phSelectUsers)}
                  isClearable
                  isSearchable
                  isMulti
                />
              </div>
            )}

            {destinationMode === 'group' && (
              <div className='mt-3'>
                <div className='mb-2 fw-semibold'>
                  {intl.formatMessage(M.assignmentGroup)} {loadingGroups && (
                    <span>
                      <Spinner animation='border' size='sm' />
                    </span>
                  )}
                </div>
                <Select<GroupOption, false>
                  value={groupTo}
                  options={groupOptions}
                  onChange={(selected) => setGroupTo(selected)}
                  placeholder={intl.formatMessage(M.phSelectGroup)}
                  isClearable
                  isSearchable
                  noOptionsMessage={() =>
                    isSupervisorOnly
                      ? intl.formatMessage(M.noOptsSupervised)
                      : intl.formatMessage(M.noOptsGeneral)
                  }
                />
                <div className='mt-2 text-muted fs-7'>
                  {isSupervisorOnly
                    ? intl.formatMessage(M.hintSupervisor)
                    : intl.formatMessage(M.hintGeneral)}
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={modalClose} variant='light'>
          {intl.formatMessage(M.cancel)}
        </Button>
        <Button onClick={handleReAssignPolicies} variant='primary' disabled={disabledAssign}>
          {loading && <Spinner animation='border' className='me-2' size='sm' />}
          <i className='bi bi-person-up' />
          {intl.formatMessage(M.reassignBtn)}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
